import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { getSupportSelector } from "../../../../slices/ExploreProducts.slice";
import { useAppSelector } from "../../../../store/hooks";
import VolumeTabCharts from "./VolumeTabCharts";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";

export const VolumeTab = () => {
  // Properties
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");
  const [SRfilteredItems, setSRFilteredItems] = useState([]);
  const [CRIfilteredItems, setCRIFilteredItems] = useState([]);

  // Change: chartFilters as arrays to handle multiple filters
  const [SRChartFilters, setSRChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const [CRIChartFilters, setCRIChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const filters = useContext(ProductViewFiltersContext);
  const { data, isLoading, error } = useAppSelector(getSupportSelector);

  // Events
  const HandleAPIExcpetion = () => (error && <DisplayAPIsException height={250} />) || "";

  const customProps = {
    productName: filters?.productName,
    productFilters: JSON.stringify(filters)
  };

  // Hooks
  useEffect(() => {
    logEvent("Volume Tab - Support", "Product View Page", "VolumeTab", customProps);
  }, []);

  useEffect(() => {
    setSRFilteredItems(data?.volumeTabSRSupportData?.data ?? []);
    setCRIFilteredItems(data?.volumeTabCRISupportData?.data ?? []);
    if (error) {
      logException(
        "Product View Page - Support Tab - Volume Sub Tab",
        "VolumeTab",
        "useGetSupportDataSummaryByProductIdMutation",
        error
      );
    }
  }, [data, error]);

  useEffect(() => {
    const filteredData = (data?.volumeTabSRSupportData?.data || []).filter(
      ({ tenantName, regionName, monthYear, serviceRequestState }: any) => {
        // If no filters, show all data
        if (SRChartFilters.length === 0) {
          return true;
        }

        // Apply all filters - item must match ALL active filters
        return SRChartFilters.every((filter) => {
          if (filter.filterKey === "monthYear") {
            return monthYear === filter.value;
          } else if (filter.filterKey === "tenantName") {
            return tenantName === filter.value;
          } else if (filter.filterKey === "regionName") {
            return regionName === filter.value;
          } else if (filter.filterKey === "serviceRequestState") {
            return serviceRequestState === filter.value;
          }
          return true;
        });
      }
    );
    setSRFilteredItems(filteredData);
  }, [SRChartFilters, data?.volumeTabSRSupportData?.data]);

  useEffect(() => {
    const filteredData = (data?.volumeTabCRISupportData?.data || []).filter(
      ({ tenantName, regionName, monthYear, status }: any) => {
        // If no filters, show all data
        if (CRIChartFilters.length === 0) {
          return true;
        }

        // Apply all filters - item must match ALL active filters
        return CRIChartFilters.every((filter) => {
          if (filter.filterKey === "monthYear") {
            return monthYear === filter.value;
          } else if (filter.filterKey === "tenantName") {
            return tenantName === filter.value;
          } else if (filter.filterKey === "regionName") {
            return regionName === filter.value;
          } else if (filter.filterKey === "status") {
            return status === filter.value;
          }
          return true;
        });
      }
    );
    setCRIFilteredItems(filteredData);
  }, [CRIChartFilters, data?.volumeTabCRISupportData?.data]);

  // Handler to manage array of filters for SR charts
  const handleSRChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setSRChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  // Handler to manage array of filters for CRI charts
  const handleCRIChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setCRIChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  return (
    <>
      <VolumeTabCharts
        loading={isLoading}
        onSelectedChartFilter={handleSRChartFilter}
        tenantId={tenantId}
        tenantName=""
        options={{
          filteredData: SRfilteredItems,
          originalData: data?.volumeTabSRSupportData?.data || [],
          chartOptions: [
            {
              id: 1,
              field: "monthYear",
              key: "product-view-sr-volume-support-chart-by-monthYear",
              title: "SR Volume Trend",
              type: "line",
              tooltipDescription: "This represents the volume distribution of Service Requests (SR) by creation date.",
              kustoLink: data?.volumeTabSRSupportData?.kustoLinks?.MonthYear,
              legendName: "Service Requests"
            },
            {
              id: 2,
              field: "tenantName",
              key: "product-view-sr-volume-support-chart-by-tenantName",
              title: "Top 10 Tenants By SR Volume",
              type: "horizontalbar",
              tooltipDescription: "This represents the tenant distribution based on the volume of Service Requests.",
              kustoLink: data?.volumeTabSRSupportData?.kustoLinks?.TenantName
            },
            {
              id: 3,
              field: "regionName",
              key: "product-view-sr-volume-support-chart-by-regionName",
              title: "Top 10 Regions By SR Volume",
              type: "horizontalbar",
              tooltipDescription:
                "This represents the distribution of support request volume by the country from which it was filed.​",
              kustoLink: data?.volumeTabSRSupportData?.kustoLinks?.RegionName
            },
            {
              id: 4,
              field: "serviceRequestState",
              key: "product-view-sr-volume-support-chart-by-serviceRequestState",
              title: "Service Request State",
              type: "donut",
              tooltipDescription: "This indicates the current state of the Service Request, i.e., open or closed.",
              kustoLink: data?.volumeTabSRSupportData?.kustoLinks?.ServiceRequestState
            }
          ]
        }}
      />

      <VolumeTabCharts
        loading={isLoading}
        onSelectedChartFilter={handleCRIChartFilter}
        tenantId={tenantId}
        tenantName=""
        options={{
          filteredData: CRIfilteredItems,
          originalData: data?.volumeTabCRISupportData?.data || [],
          chartOptions: [
            {
              id: 1,
              field: "monthYear",
              key: "customer-view-customer-engagements-support-chart-by-monthYear",
              title: "CRI Volume Trend",
              type: "line",
              tooltipDescription:
                "This represents the volume distribution of Customer Reported Incidents (CRIs) by creation date.",
              kustoLink: data?.volumeTabCRISupportData?.kustoLinks?.MonthYear,
              legendName: "Reported Incidents by Customers"
            },
            {
              id: 2,
              field: "tenantName",
              key: "customer-view-customer-engagements-support-chart-by-tenantName",
              title: "Top 10 Tenants By CRI Volume",
              type: "horizontalbar",
              tooltipDescription:
                "This represents the tenant distribution based on the volume of Customer Reported Incidents.",
              kustoLink: data?.volumeTabCRISupportData?.kustoLinks?.TenantName
            },
            {
              id: 3,
              field: "regionName",
              key: "customer-view-customer-engagements-support-chart-by-regionName",
              title: "Top 10 Regions By CRI Volume",
              type: "horizontalbar",
              tooltipDescription:
                "This represents the volume distribution of Customer Reported Incidents based on the country where the relevant Service Request was filed.",
              kustoLink: data?.volumeTabCRISupportData?.kustoLinks?.RegionName
            },
            {
              id: 4,
              field: "status",
              key: "customer-view-customer-engagements-support-chart-by-serviceRequestState",
              title: "ICM State",
              type: "donut",
              tooltipDescription: "Shows how ICM incidents status are distributed.",
              kustoLink: data?.volumeTabCRISupportData?.kustoLinks?.Status
            }
          ]
        }}
      />
      <HandleAPIExcpetion />
    </>
  );
};
